// DamagesTable.tsx — Schaden-Übersicht (/damages) im „Refined Classic"-Redesign,
// baugleich zur Kontakte- und Vorgangs-Tabelle.
//
// Features (über die generische DataTable-Engine + Hooks):
//  - Kopfleiste wie /contacts und /vorgaenge: Suche links, direkt daneben der
//    primäre Neu-Button (vorher stand er ganz rechts, und die Suche trug ein
//    eigenes, blasses Toolbar-Styling)
//  - Infinite-Scroll (offset/limit, GET /damage, X-Total-Count) via useInfiniteRows
//  - per-Spalte Filter (Text, Status-Dropdown, Datumsbereiche) — serverseitig
//  - per-Spalte Sortierung, serverseitig über die Backend-Allowlist
//  - Spalten verschieben / aus-/einblenden / schmal zusammenklappen (persistiert)
//  - Jahr-Vorfilter (mit Anzahl je Jahr) in der Sub-Leiste; Default bleibt das
//    neueste Jahr mit Daten
//  - Telefon (≤ 768 px): Karten statt Tabelle, Status als Auswahl in der
//    Sub-Leiste (die Spaltenfilter gibt es dort nicht)

import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { I } from "./redesign/kvpIcons";
import {
  DataTable,
  ColumnsMenu,
  type Column,
  type DateRangeValue,
  type SortState,
  Dash,
} from "./redesign/table";
import "./redesign/contactRedesign.css";
import { useResponsive } from "../hooks/useResponsive";
import { SchadenKarten, StatusPill } from "./schaden/SchadenKarten";
import { apiFetch } from "../utils/apiFetch";
import { useDebouncedValue } from "../utils/useDebouncedValue";
import { useInfiniteRows } from "../hooks/useInfiniteRows";
import { useTableNavState } from "../hooks/useTableNavState";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openSchadenFormWindow } from "./windowManager/SchadenFormWindow";
import { SchadenlistenMenue } from "./schadenlisten/SchadenlistenMenue";
import {
  getCurrentUserPreferenceKey,
  fetchUserPreferences,
  saveUserPreference,
  DAMAGE_TABLE_COLUMN_ORDER_SCOPE,
  DAMAGE_TABLE_COLUMN_VISIBILITY_SCOPE,
  DAMAGE_TABLE_COLUMN_WIDTHS_SCOPE,
} from "../utils/userPreferences";

/* ────────────────────── Types ────────────────────── */

export type Schaden = {
  SchadenID: number;
  Status: string;
  Schadennr: string;
  KontaktName: string;
  Vertragsnummer: string;
  Schadensart: string;
  RisikoOrt: string;
  SchadenDatum: string;
  MeldeDatum: string;
  // Legacy-Felder: weiterhin von der alten Bearbeiten-Maske (/schaeden/edit/:id,
  // DamagesForm.tsx) gelesen — optional gehalten, damit diese ohne Änderung kompiliert.
  Berater?: string;
  Zweig?: string;
  schaden?: string;
  schadenhoehe?: string;
  anspruchsteller?: string;
  vorgangsNr?: string;
};

type YearOption = { year: number; count: number };

/** Persistierter Tabellen-Zustand für den Rücksprung aus der Schadenmappe. */
type TableSnapshot = {
  search: string;
  filterValues: Record<string, string | boolean | undefined>;
  dateRangeValues: Record<string, DateRangeValue | undefined>;
  sort: SortState | null;
  /** null = „Alle Jahre"; Zahl = konkretes Jahr. */
  year: number | null;
  scrollTop: number;
  loadedCount: number;
};

/* ────────────────────── Status ────────────────────── */

/** Werte des Status-Spaltenfilters; sie gehen als `status` ans Backend. */
const STATUS_FILTER_OPTIONS = ["Offen", "Geschlossen"];

/* ────────────────────── Datums-Helpers ────────────────────── */

/**
 * Importierte Schäden tragen nur ein Jahr (Datum wird auf 1.1. gesetzt). Zeige in
 * diesem Fall nur das Jahr, sonst das echte Datum; fällt auf das Jahr-Feld zurück.
 */
const fmtYearOrDate = (dateStr?: string | null, year?: number | null): string => {
  if (dateStr) {
    const d = new Date(dateStr);
    if (!Number.isNaN(d.getTime())) {
      const isYearOnly =
        d.getUTCMonth() === 0 && d.getUTCDate() === 1 && (!year || d.getUTCFullYear() === year);
      return isYearOnly ? String(year ?? d.getUTCFullYear()) : d.toLocaleDateString("de-DE");
    }
  }
  return year ? String(year) : "";
};

const contactName = (c: any): string => {
  if (!c) return "";
  if (c.displayName) return String(c.displayName);
  const parts = [c.firstname, c.firstName, c.lastname].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return c.company ? String(c.company) : "";
};

const mapDamage = (d: any): Schaden => ({
  SchadenID: Number(d.id ?? d.SchadenID ?? 0),
  Status: String(d.status ?? d.Status ?? ""),
  Schadennr: String(d.damageNbr ?? d.externalDamageNbr ?? ""),
  KontaktName: contactName(d.contact) || String(d.KontaktName ?? ""),
  Vertragsnummer: String(d.contract?.contractNbr ?? d.Vertragsnummer ?? ""),
  Schadensart: String(d.damageType ?? d.Schadensart ?? ""),
  RisikoOrt: String(d.contract?.riskLocation ?? d.RisikoOrt ?? ""),
  SchadenDatum: fmtYearOrDate(d.damageDate, d.damageYear),
  MeldeDatum: fmtYearOrDate(d.reportDate, d.reportYear),
});

/* ────────────────────── Spalten-Reihenfolge ────────────────────── */

/** Reihenfolge-fähige (nicht-pinned) Spalten-Ids in Default-Reihenfolge. */
// Status + Schaden-Nr. sind pinned/eingefroren (führend, nicht verschiebbar) →
// nicht Teil der reorder-fähigen Order.
const DEFAULT_ORDER = [
  "KontaktName",
  "Vertragsnummer",
  "Schadensart",
  "RisikoOrt",
  "SchadenDatum",
  "MeldeDatum",
];
const ORDER_SET = new Set(DEFAULT_ORDER);

const isValidOrder = (v: unknown): v is string[] =>
  Array.isArray(v) &&
  v.length === DEFAULT_ORDER.length &&
  v.every((id) => typeof id === "string" && ORDER_SET.has(id));

/** Query-Parameter-Name je Spalten-Filter (Backend-Vertrag). */
const FILTER_PARAM: Record<string, string> = {
  Status: "status",
  Schadennr: "schadennr",
  KontaktName: "kontakt",
  Vertragsnummer: "vertrag",
  Schadensart: "schadensart",
  RisikoOrt: "risikoOrt",
};

/** Query-Parameter-Paar je Datumsbereich-Filter. */
const DATE_PARAMS: Record<string, { von: string; bis: string }> = {
  SchadenDatum: { von: "schadenVon", bis: "schadenBis" },
  MeldeDatum: { von: "meldeVon", bis: "meldeBis" },
};

/* ────────────────────── Component ────────────────────── */

const DamagesTable: React.FC = () => {
  const navigate = useNavigate();
  const { openWindow } = useWindowManager();
  const { isPhone } = useResponsive();

  // Zustand beim Rücksprung aus der Schadenmappe wiederherstellen.
  const { initial: restored, save: saveNavState } = useTableNavState<TableSnapshot>("damages", {
    isDetailRoute: (p) => /^\/schaeden\/\d+(\/|$)/.test(p) || /^\/schaeden\/edit\/\d+/.test(p),
  });
  const scrollRef = useRef<HTMLDivElement>(null);

  // --- Such-/Filter-/Sort-State (ggf. aus Snapshot vorbelegt) ---
  const [search, setSearch] = useState(restored?.search ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [filterValues, setFilterValues] = useState<Record<string, string | boolean | undefined>>(
    restored?.filterValues ?? {},
  );
  const [dateRangeValues, setDateRangeValues] = useState<Record<string, DateRangeValue | undefined>>(
    restored?.dateRangeValues ?? {},
  );
  const [sort, setSort] = useState<SortState | null>(restored?.sort ?? null);

  // --- Jahr-Vorfilter ---
  // undefined = noch nicht initialisiert (Jahre laden läuft); null = „Alle Jahre".
  // Aus einem Snapshot kommt bereits ein konkreter Wert — dann nicht überschreiben.
  const [years, setYears] = useState<YearOption[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null | undefined>(
    restored ? restored.year : undefined,
  );

  // --- Spalten: Reihenfolge / Sichtbarkeit / Breiten / Collapse ---
  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_ORDER);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>([]);

  // Jahre laden → Default = neuestes Jahr mit Daten (nur ohne Snapshot).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiFetch<YearOption[]>("damage/years");
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setYears(list);
        setSelectedYear((prev) => (prev === undefined ? (list[0]?.year ?? null) : prev));
      } catch {
        if (!cancelled) setSelectedYear((prev) => (prev === undefined ? null : prev));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Daten (Infinite-Scroll) ----
  // Alle Spaltenfilter als flache Query-Parameter; der Serialisierungs-String
  // dient zugleich als Reload-Dependency.
  const filterParams = useMemo(() => {
    const params: Record<string, string> = {};
    for (const [columnId, value] of Object.entries(filterValues)) {
      const key = FILTER_PARAM[columnId];
      if (!key || value === undefined || value === "" || value === false) continue;
      // Der Status-Filter spricht das Backend kleingeschrieben an („offen“/
      // „geschlossen“) — dieselbe Semantik wie der frühere Segmented-Vorfilter.
      params[key] = columnId === "Status" ? String(value).toLowerCase() : String(value);
    }
    for (const [columnId, range] of Object.entries(dateRangeValues)) {
      const keys = DATE_PARAMS[columnId];
      if (!keys || !range) continue;
      if (range.von?.trim()) params[keys.von] = range.von.trim();
      if (range.bis?.trim()) params[keys.bis] = range.bis.trim();
    }
    return params;
  }, [filterValues, dateRangeValues]);
  const filterParamsKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);

  const { rows, total, hasMore, loading, loadMore } = useInfiniteRows<Schaden>({
    getKey: (r) => r.SchadenID,
    deps: [
      debouncedSearch,
      filterParamsKey,
      sort?.field ?? "",
      sort?.direction ?? "",
      // `undefined` (Jahre noch nicht geladen) unterscheidet sich von `null`
      // („Alle Jahre“) — sonst liefe ein Erst-Load mit falschem Vorfilter.
      String(selectedYear),
    ],
    initialOffset: restored ? Math.min(restored.loadedCount, 300) : undefined,
    fetchPage: async ({ limit, offset, signal }) => {
      // Warten, bis der Jahr-Default steht — sonst zeigt der erste Frame alle
      // Jahre und springt danach auf das neueste um.
      if (selectedYear === undefined) return { rows: [], total: null };
      const { data, total: t } = await apiFetch<any[]>("damage", {
        signal,
        params: {
          limit,
          offset,
          q: debouncedSearch,
          ...(typeof selectedYear === "number" ? { year: selectedYear } : {}),
          ...filterParams,
          sort: sort?.field ?? "",
          direction: sort?.direction ?? "",
        },
      });
      return { rows: (Array.isArray(data) ? data : []).map(mapDamage), total: t };
    },
  });

  // ---- Navigations-Zustand sichern + Scroll wiederherstellen ----
  const latestSnapRef = useRef<Omit<TableSnapshot, "scrollTop">>({
    search,
    filterValues,
    dateRangeValues,
    sort,
    year: selectedYear ?? null,
    loadedCount: rows.length,
  });
  latestSnapRef.current = {
    search,
    filterValues,
    dateRangeValues,
    sort,
    year: selectedYear ?? null,
    loadedCount: rows.length,
  };
  const persistNav = useCallback(() => {
    saveNavState({ ...latestSnapRef.current, scrollTop: scrollRef.current?.scrollTop ?? 0 });
  }, [saveNavState]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(persistNav);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [persistNav]);
  useEffect(() => () => persistNav(), [persistNav]); // beim Verlassen (→ Mappe) sichern
  const restoredScrollRef = useRef(false);
  useLayoutEffect(() => {
    if (restoredScrollRef.current) return;
    if (!restored) {
      restoredScrollRef.current = true;
      return;
    }
    if (rows.length >= Math.min(restored.loadedCount, 300)) {
      if (scrollRef.current) scrollRef.current.scrollTop = restored.scrollTop;
      restoredScrollRef.current = true;
    }
  }, [rows.length, restored]);

  // ---- Spalten-Präferenzen laden/speichern ----
  const prefKeyRef = useRef<string | null>(null);
  const hydratedRef = useRef(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const key = await getCurrentUserPreferenceKey();
      if (!alive) return;
      prefKeyRef.current = key;
      if (key) {
        try {
          const prefs = await fetchUserPreferences([
            DAMAGE_TABLE_COLUMN_ORDER_SCOPE,
            DAMAGE_TABLE_COLUMN_VISIBILITY_SCOPE,
            DAMAGE_TABLE_COLUMN_WIDTHS_SCOPE,
          ]);
          const ord = prefs[DAMAGE_TABLE_COLUMN_ORDER_SCOPE] as string[] | undefined;
          const vis = prefs[DAMAGE_TABLE_COLUMN_VISIBILITY_SCOPE] as Record<string, boolean> | undefined;
          const widths = prefs[DAMAGE_TABLE_COLUMN_WIDTHS_SCOPE] as Record<string, number> | undefined;
          if (!alive) return;
          if (isValidOrder(ord)) setColumnOrder(ord);
          if (vis && typeof vis === "object") {
            const clean: Record<string, boolean> = {};
            for (const id of DEFAULT_ORDER) if (vis[id] === false) clean[id] = false;
            setColumnVisibility(clean);
          }
          if (widths && typeof widths === "object") {
            const clean: Record<string, number> = {};
            for (const [id, w] of Object.entries(widths)) {
              if (typeof w === "number" && Number.isFinite(w) && w > 0) clean[id] = w;
            }
            setColumnWidths(clean);
          }
        } catch {
          /* Defaults beibehalten */
        }
      }
      hydratedRef.current = true;
    })();
    return () => {
      alive = false;
    };
  }, []);
  // Ersten Lauf nach der Hydration NICHT speichern — nur den Ausgangsstand merken;
  // danach bei jeder echten Änderung (entprellt).
  const savedSnapshotRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hydratedRef.current || !prefKeyRef.current) return;
    const snapshot = JSON.stringify([columnOrder, columnVisibility, columnWidths]);
    if (savedSnapshotRef.current === null) {
      savedSnapshotRef.current = snapshot;
      return;
    }
    if (snapshot === savedSnapshotRef.current) return;
    savedSnapshotRef.current = snapshot;
    const key = prefKeyRef.current;
    const t = setTimeout(() => {
      saveUserPreference(key, DAMAGE_TABLE_COLUMN_ORDER_SCOPE, columnOrder).catch(() => {});
      saveUserPreference(key, DAMAGE_TABLE_COLUMN_VISIBILITY_SCOPE, columnVisibility).catch(() => {});
      saveUserPreference(key, DAMAGE_TABLE_COLUMN_WIDTHS_SCOPE, columnWidths).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [columnOrder, columnVisibility, columnWidths]);

  // ---- Handler ----
  const updateFilter = useCallback((columnId: string, value: string | boolean | undefined) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      if (value === undefined || value === "" || value === false) delete next[columnId];
      else next[columnId] = value;
      return next;
    });
  }, []);

  const updateDateRange = useCallback((columnId: string, next: DateRangeValue) => {
    setDateRangeValues((prev) => {
      const merged = { ...prev };
      if (!next.von?.trim() && !next.bis?.trim()) delete merged[columnId];
      else merged[columnId] = next;
      return merged;
    });
  }, []);

  const toggleCollapsed = useCallback(
    (id: string) =>
      setCollapsedColumns((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [],
  );

  const openSchaden = (item: Schaden) => navigate(`/schaeden/${item.SchadenID}`);

  // ---- Spalten ----
  const columns: Column<Schaden>[] = useMemo(
    () => [
      {
        id: "Status",
        header: "Status",
        pinned: true,
        frozen: true,
        width: 140,
        minWidth: 118,
        sortable: true,
        filter: { kind: "dropdown", options: STATUS_FILTER_OPTIONS },
        cell: (r) => <StatusPill status={r.Status} />,
      },
      {
        id: "Schadennr",
        header: "Schaden-Nr.",
        className: "name",
        pinned: true,
        frozen: true,
        width: 165,
        minWidth: 120,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        cell: (r) => r.Schadennr || <Dash />,
      },
      {
        id: "KontaktName",
        header: "Kontakt",
        className: "kt-wrap",
        minWidth: 180,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.KontaktName || <Dash />,
      },
      {
        id: "Vertragsnummer",
        header: "Vertrag",
        minWidth: 130,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.Vertragsnummer || <Dash />,
      },
      {
        id: "Schadensart",
        header: "Schadensart",
        className: "kt-wrap",
        minWidth: 150,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.Schadensart || <Dash />,
      },
      {
        id: "RisikoOrt",
        header: "Risiko / Ort",
        className: "kt-wrap",
        minWidth: 170,
        resizable: true,
        sortable: true,
        filter: { kind: "text" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.RisikoOrt || <Dash />,
      },
      {
        id: "SchadenDatum",
        header: "Schadentag",
        width: 130,
        minWidth: 100,
        resizable: true,
        sortable: true,
        filter: { kind: "dateRange" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.SchadenDatum || <Dash />,
      },
      {
        id: "MeldeDatum",
        header: "Meldedatum",
        width: 130,
        minWidth: 100,
        resizable: true,
        sortable: true,
        filter: { kind: "dateRange" },
        hideable: true,
        collapsible: true,
        cell: (r) => r.MeldeDatum || <Dash />,
      },
    ],
    [],
  );

  // ---- Sub-Leiste über der Tabelle: Zähler · Jahr-Vorfilter · Spaltenmenü ----
  const tableSubBar = (
    <div className="kt-subbar">
      <span style={{ marginRight: "auto", fontSize: 12.5, fontWeight: 600, color: "var(--ink-3)" }}>
        {total != null ? `${total} ${total === 1 ? "Schaden" : "Schäden"}` : ""}
      </span>
      <label
        style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 600, color: "var(--ink-3)" }}
      >
        Schadenjahr
        <select
          className="sel"
          // `.sel` ist als Formularfeld gestylt (41px hoch, 100% breit) — in der
          // Leiste sitzt es neben Buttons und braucht deren Maß.
          style={{ height: 34, width: "auto", minWidth: 132, padding: "0 30px 0 11px", fontSize: 13.5 }}
          value={typeof selectedYear === "number" ? String(selectedYear) : "alle"}
          onChange={(e) => setSelectedYear(e.target.value === "alle" ? null : Number(e.target.value))}
          aria-label="Schadenjahr"
        >
          <option value="alle">Alle Jahre</option>
          {years.map((y) => (
            <option key={y.year} value={String(y.year)}>
              {y.year} ({y.count})
            </option>
          ))}
        </select>
      </label>
      {isPhone ? (
        // Am Telefon gibt es keine Spaltenköpfe — der Status-Filter sitzt hier.
        <select
          className="sel"
          style={{ height: 34, width: "auto", minWidth: 120, padding: "0 30px 0 11px", fontSize: 13.5 }}
          value={typeof filterValues.Status === "string" ? filterValues.Status : ""}
          onChange={(e) => updateFilter("Status", e.target.value || undefined)}
          aria-label="Status"
        >
          <option value="">Alle Status</option>
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <ColumnsMenu
          columns={columns}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          collapsedColumns={collapsedColumns}
          onToggleCollapsed={toggleCollapsed}
        />
      )}
    </div>
  );

  return (
    <div className="kvp-rd kvp-rd-table-page">
      {/* Kopfleiste wie /contacts und /vorgaenge: Suche, direkt daneben „+ Schaden". */}
      <div className="bar">
        <div className="search">
          {I.search}
          <input
            placeholder="Nach Schaden-Nr., Kontakt, Schadensart suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button type="button" className="tbtn primary" onClick={() => openSchadenFormWindow(openWindow)}>
          {I.plus}Schaden
        </button>
        <SchadenlistenMenue />
      </div>

      {isPhone ? (
        <>
          {tableSubBar}
          <SchadenKarten
            karten={rows.map((r) => ({
              id: r.SchadenID,
              status: r.Status,
              schadennr: r.Schadennr,
              kontakt: r.KontaktName,
              vertrag: r.Vertragsnummer,
              schadensart: r.Schadensart,
              risikoOrt: r.RisikoOrt,
              schadenDatum: r.SchadenDatum,
              meldeDatum: r.MeldeDatum,
            }))}
            onOpen={(id) => navigate(`/schaeden/${id}`)}
            emptyText={
              loading || selectedYear === undefined ? "Lade Schäden …" : "Keine Schäden für diese Filter."
            }
            hasMore={hasMore}
            loading={loading}
            onLoadMore={loadMore}
            scrollRef={scrollRef}
            scrollId="damages-table-scroll"
          />
        </>
      ) : (
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.SchadenID}
        onRowClick={openSchaden}
        // Solange der Jahr-Default noch aussteht, ist die Liste leer, ohne dass
        // ein Filter etwas ausgeschlossen hätte — dann nicht „keine Treffer" melden.
        emptyText={loading || selectedYear === undefined ? "Lade Schäden …" : "Keine Schäden für diese Filter."}
        sort={sort}
        onSortChange={setSort}
        filterValues={filterValues}
        onFilterChange={updateFilter}
        dateRangeValues={dateRangeValues}
        onDateRangeChange={updateDateRange}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={toggleCollapsed}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
        loadingText="Lade Schäden …"
        scrollContainerId="damages-table-scroll"
        scrollRef={scrollRef}
        aboveTable={tableSubBar}
      />
      )}
    </div>
  );
};

export default DamagesTable;
